import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import Chart from "react-apexcharts";
import { getMilestoneTrend, getUpdateRunList } from "../../api/process";
import SidePanel from "../../components/common/layout/SidePanel";
import MilestoneSelector from "../../components/process/MilestoneSelector";
import { ChevronDown, ChevronUp, Filter, Calendar, Maximize2, X } from "lucide-react";

export default function MTAChart() {
  const [milestones, setMilestones] = useState([]);
  const [projectPeriod, setProjectPeriod] = useState({ start: 0, end: 0 });
  const [selectedIds, setSelectedIds] = useState([]);
  const [isPanelOpen, setIsPanelOpen] = useState(false);
  const [groups, setGroups] = useState([]);
  const { id: projectId } = useParams();
  const [showDetails, setShowDetails] = useState(false);
  const [baselineRuns, setBaselineRuns] = useState([]);
  const [selectedBaseline, setSelectedBaseline] = useState(null);
  const [isChartExpanded, setIsChartExpanded] = useState(false);
  const [viewportSize, setViewportSize] = useState({ width: 375, height: 667 }); // 기본값: iPhone SE

  // Viewport 크기 감지 (2025 트렌드: 동적 크기 조정)
  useEffect(() => {
    const updateViewport = () => {
      setViewportSize({ width: window.innerWidth, height: window.innerHeight });
    };
    updateViewport();
    window.addEventListener('resize', updateViewport);
    return () => window.removeEventListener('resize', updateViewport);
  }, []);

  useEffect(() => {
    getUpdateRunList().then((runs) => {
      setBaselineRuns(runs.filter((r) => r.run_type === "B"));

      // ✅ 기본값: 가장 최근 항목
      if (runs.length > 0) {
        const latestBaseline = runs.find((r) => r.run_type === "B");
        setSelectedBaseline(latestBaseline?.id || null);
      }
    });
  }, []);

  useEffect(() => {
    getMilestoneTrend(selectedBaseline).then((res) => {
      const groups = res.wbs_groups || [];
      const allMilestones = groups.flatMap((g) => g.milestones || []);

      setMilestones(allMilestones);
      setGroups(groups);
      setProjectPeriod({
        start: new Date(res.project.period_start).getTime(),
        end:
          new Date(res.project.period_finish).getTime() +
          30 * 24 * 60 * 60 * 1000,
      });

      const allIds = groups.flatMap((g) => g.milestones.map((m) => m.code));
      setSelectedIds(allIds);
    });
  }, [projectId, selectedBaseline]);

  // 로딩 상태 처리
  if (!milestones.length) {
    return (
      <div className="mobile-page-root mb-16">
        <div className="mobile-page-root-inner">
          <div className="bg-white rounded-lg p-6 border text-center">
            <div className="w-10 h-10 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto mb-3" />
            <p className="text-sm text-gray-500">MTA 데이터 로딩 중...</p>
          </div>
        </div>
      </div>
    );
  }

  // ✅ 선택된 마일스톤만 필터링
  const filtered = milestones.filter((ms) => selectedIds.includes(ms.code));

  // ✅ 각 마일스톤 시리즈 생성 + 상태별 색상 + 툴팁 정보
  const milestoneSeries = filtered.map((ms) => {
    const points = ms.updates
      .map((u) => ({
        x: u.cutoff === 0 ? projectPeriod.start : new Date(u.cutoff).getTime(),
        y: new Date(u.forecast).getTime(),
      }))
      .sort((a, b) => a.x - b.x);

    const clipped = [];

    for (let i = 0; i < points.length; i++) {
      const curr = points[i];
      if (i === 0) {
        clipped.push(curr);
        continue;
      }

      const prev = points[i - 1];
      if (curr.y >= curr.x) {
        clipped.push(curr);
        continue;
      }

      const t = (prev.x - prev.y) / (curr.y - prev.y - (curr.x - prev.x));
      const x_cross = prev.x + (curr.x - prev.x) * t;
      const y_cross = x_cross;
      clipped.push({ x: x_cross, y: y_cross });
      break;
    }

    // ✅ 상태별 색상 계산 (Baseline 기준)
    const baseline = new Date(ms.baseline).getTime(); // 기준선 finish
    const lastForecast = new Date(ms.updates.at(-1)?.forecast).getTime();
    const diffDays = Math.round(
      (lastForecast - baseline) / (1000 * 60 * 60 * 24)
    );

    let color = "#22c55e"; // 기본: 정상(초록)
    let status = "정상";
    if (diffDays > 0) {
      color = "#ef4444"; // 지연(빨강)
      status = `지연 (${diffDays}일)`;
    } else if (diffDays < 0) {
      color = "#3b82f6"; // 조기(파랑)
      status = `조기 (${Math.abs(diffDays)}일)`;
    } else {
      status = "정상";
    }

    // ✅ 각 포인트에 툴팁용 상태 추가
    const dataWithStatus = clipped.map((p) => ({ ...p, status }));

    return { name: ms.name, data: dataWithStatus, color, status };
  });

  // ✅ 대각선 (Completion Line)
  const completionLine = {
    name: "Completion Line",
    data: [
      { x: projectPeriod.start, y: projectPeriod.start },
      { x: projectPeriod.end, y: projectPeriod.end },
    ],
    color: "#ef4444",
  };

  // ✅ ApexCharts 옵션 (모바일 최적화)
  const options = {
    chart: {
      type: "line",
      animations: { enabled: true, easing: "easeinout", speed: 400 },
      zoom: { enabled: false },
      toolbar: { show: false }, // 모바일에서 툴바 숨김
    },
    markers: showDetails
      ? {
        size: 4, // 포인트 보이게
        hover: { size: 7 },
      }
      : {
        size: 0, // 선만 표시
        hover: { size: 0 },
      },

    grid: {
      padding: { left: 10, right: 10 },
      xaxis: { lines: { show: true } },
      yaxis: { lines: { show: true } },
    },
    stroke: {
      width: [...milestoneSeries.map(() => 2), 2],
      curve: "straight",
      dashArray: [...milestoneSeries.map(() => 0), 0],
    },
    colors: milestoneSeries.map((s) => s.color).concat("#ef4444"),
    legend: { show: false },

    // ✅ 툴팁 커스텀
    tooltip: {
      enabled: showDetails,
      shared: false,
      intersect: true,
      followCursor: true,
      custom: ({ series, seriesIndex, dataPointIndex, w }) => {
        const seriesItem = w.config.series[seriesIndex];
        const point = seriesItem.data[dataPointIndex];
        if (!point) return "";

        // ✅ baseline & forecast 날짜 계산
        const baseline = new Date(
          filtered.find((ms) => ms.name === seriesItem.name)?.baseline
        ).toLocaleDateString("ko-KR", {
          year: "numeric",
          month: "short",
          day: "numeric",
        });

        const forecast = new Date(point.y).toLocaleDateString("ko-KR", {
          year: "numeric",
          month: "short",
          day: "numeric",
        });

        const status = point.status || "정보 없음";

        return `
      <div style="
        padding:6px 10px;
        border-radius:8px;
        background:#fff;
        box-shadow:0 0 6px rgba(0,0,0,0.1);
        font-size:12px;
        line-height:1.4;
      ">
        <strong>${seriesItem.name}</strong><br/>
        Baseline 완료일: <b>${baseline}</b><br/>
        현재 예측 완료일: <b>${forecast}</b><br/>
        최근 상태: <b>${status}</b>
      </div>
    `;
      },
    },
    xaxis: {
      type: "datetime",
      position: "top",
      min: projectPeriod.start,
      max: projectPeriod.end,
      tickAmount: 8, // 모바일: 틱 수 줄임
      title: { text: "Cut-off Date", style: { fontSize: "10px" } },
      labels: {
        style: { fontSize: "9px", colors: "#9CA3AF" },
        formatter: (val) => {
          const d = new Date(val);
          return `${String(d.getFullYear()).slice(2)}/${String(
            d.getMonth() + 1
          ).padStart(2, "0")}`;
        },
      },
    },
    yaxis: {
      type: "datetime",
      min: projectPeriod.start,
      max: projectPeriod.end,
      tickAmount: 8, // 모바일: 틱 수 줄임
      labels: {
        style: { fontSize: "9px", colors: "#374151" },
        formatter: (val) => {
          const d = new Date(val);
          return `${String(d.getFullYear()).slice(2)}/${String(
            d.getMonth() + 1
          ).padStart(2, "0")}`;
        },
      },
    },
  };

  return (
    <div className="mobile-page-root mb-16">
      <div className="mobile-page-root-inner space-y-3">
        {/* 헤더 */}
        <div className="bg-white rounded-lg p-3 border">
          <h2 className="text-lg font-bold text-gray-900">MTA 분석</h2>
          <p className="text-xs text-gray-500 mt-0.5">마일스톤 추세 분석</p>
        </div>

        {/* 컨트롤 영역 */}
        <div className="bg-white rounded-lg p-3 border space-y-3">
          {/* 기준선 선택 */}
          <div className="space-y-1.5">
            <label className="text-xs font-medium text-gray-500">
              기준선 선택
            </label>
            <select
              value={selectedBaseline || ""}
              onChange={(e) => setSelectedBaseline(e.target.value)}
              className="w-full bg-gray-50 border border-gray-200 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              {baselineRuns.map((r) => (
                <option key={r.id} value={r.id}>
                  Rev.{r.revision || "Unnamed"}
                </option>
              ))}
            </select>
          </div>

          {/* 마일스톤 선택 & 상세보기 */}
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setIsPanelOpen(true)}
              className="flex-1 min-w-0 flex items-center justify-center gap-2 px-3 py-2 bg-blue-50 text-blue-700 rounded-lg text-sm font-medium hover:bg-blue-100 active:bg-blue-200 transition-colors"
            >
              <Filter className="w-4 h-4 flex-shrink-0" />
              <span className="truncate">마일스톤 선택 ({selectedIds.length}/{milestones.length})</span>
            </button>

            {/* 상세보기 토글 */}
            <label className="flex items-center gap-1.5 cursor-pointer select-none px-3 py-2 bg-gray-50 rounded-lg flex-shrink-0 whitespace-nowrap">
              <input
                type="checkbox"
                className="accent-blue-500 w-3.5 h-3.5 flex-shrink-0"
                checked={showDetails}
                onChange={() => setShowDetails(!showDetails)}
              />
              <span className="text-xs text-gray-600">상세</span>
            </label>
          </div>
        </div>

        {/* 차트 영역 */}
        <div className="bg-white rounded-lg border overflow-hidden">
          <div className="bg-white px-3 py-2 flex items-center justify-between border-b">
            <div className="flex items-center gap-2 min-w-0 flex-1">
              <div className="w-8 h-8 bg-gray-100 rounded-lg flex items-center justify-center flex-shrink-0">
                <Calendar className="w-4 h-4 text-gray-600" />
              </div>
              <div className="min-w-0 flex-1">
                <div className="text-sm font-semibold text-gray-900 truncate">MTA 차트</div>
                <div className="text-xs text-gray-500 truncate">{filtered.length}개 마일스톤</div>
              </div>
            </div>
            <button
              onClick={() => setIsChartExpanded(true)}
              className="flex-shrink-0 w-8 h-8 bg-blue-50 hover:bg-blue-100 rounded-lg flex items-center justify-center transition-all active:scale-95"
            >
              <Maximize2 className="w-4 h-4 text-blue-600" />
            </button>
          </div>

          <div className="p-3">
            <div className="text-center text-xs text-gray-500 mb-2">
              📱 확대 버튼으로 전체 보기
            </div>
            <div className="w-full overflow-x-auto" style={{ maxHeight: '50vh' }}>
              <div className="w-full">
                <Chart
                  options={options}
                  series={[...milestoneSeries, completionLine]}
                  type="line"
                  width="100%"
                  height={Math.min(250, viewportSize.height * 0.35)}
                />
              </div>
            </div>
          </div>
        </div>

        {/* 범례 */}
        <div className="bg-white rounded-lg border p-3">
          <div className="text-xs font-medium text-gray-500 mb-2">범례</div>
          <div className="flex flex-wrap gap-2">
            <div className="flex items-center gap-1.5">
              <div className="w-3 h-0.5 bg-green-500" />
              <span className="text-xs text-gray-600">정상</span>
            </div>
            <div className="flex items-center gap-1.5">
              <div className="w-3 h-0.5 bg-blue-500" />
              <span className="text-xs text-gray-600">조기</span>
            </div>
            <div className="flex items-center gap-1.5">
              <div className="w-3 h-0.5 bg-red-500" />
              <span className="text-xs text-gray-600">지연</span>
            </div>
            <div className="flex items-center gap-1.5">
              <div className="w-3 h-0.5 bg-red-500" />
              <span className="text-xs text-gray-600">C-Line</span>
            </div>
          </div>
        </div>

        {/* 그룹 정보 아코디언 */}
        {groups.length > 0 && (
          <div className="bg-white rounded-lg border overflow-hidden">
            <button
              type="button"
              onClick={() => setShowDetails((prev) => !prev)}
              className="w-full flex items-center justify-between px-3 py-2.5 text-left active:bg-gray-50 transition-colors min-w-0 overflow-x-hidden"
            >
              <span className="text-sm font-medium text-gray-700 truncate min-w-0 flex-1">그룹별 마일스톤 정보</span>
              {showDetails ? (
                <ChevronUp className="w-4 h-4 text-gray-400 flex-shrink-0 ml-2" />
              ) : (
                <ChevronDown className="w-4 h-4 text-gray-400 flex-shrink-0 ml-2" />
              )}
            </button>

            {showDetails && (
              <div className="px-3 pb-3 space-y-2 border-t min-w-0 overflow-x-hidden">
                {groups.map((g) => (
                  <div key={g.wbs_name} className="bg-gray-50 rounded-lg p-3 border min-w-0 overflow-x-hidden" style={{ width: '100%', maxWidth: '100%', minWidth: 0, boxSizing: 'border-box' }}>
                    <p className="text-xs font-semibold text-gray-700 mb-1 truncate">{g.wbs_name}</p>
                    <div className="space-y-1 min-w-0">
                      {g.milestones.map((m) => (
                        <p key={m.code} className="text-xs text-gray-500 truncate">• {m.name}</p>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* 마일스톤 선택 패널 */}
        <SidePanel
          isOpen={isPanelOpen}
          onClose={() => setIsPanelOpen(false)}
          title="마일스톤 선택"
          width={320}
          side="right"
        >
          <MilestoneSelector
            allMilestones={milestones}
            selectedIds={selectedIds}
            onChange={setSelectedIds}
          />
        </SidePanel>

        {/* 차트 확대 모달 */}
        {isChartExpanded && (
          <div
            className="fixed inset-0 z-50 bg-black/95 flex items-center justify-center p-2 sm:p-4 animate-fadeIn"
            onClick={() => setIsChartExpanded(false)}
            style={{ width: '100%', maxWidth: '100vw', overflowX: 'hidden' }}
          >
            <div
              className="w-full max-w-[98vw] sm:max-w-4xl md:max-w-5xl bg-white rounded-lg sm:rounded-2xl shadow-2xl overflow-hidden"
              onClick={(e) => e.stopPropagation()}
              style={{ width: '100%', maxWidth: '98vw', minWidth: 0, boxSizing: 'border-box' }}
            >
              {/* 헤더 */}
              <div className="bg-white px-3 py-2 sm:px-4 sm:py-3 flex items-center justify-between border-b min-w-0 overflow-x-hidden">
                <div className="flex items-center gap-2 min-w-0 flex-1">
                  <div className="w-7 h-7 sm:w-8 sm:h-8 bg-gray-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Calendar className="w-4 h-4 sm:w-5 sm:h-5 text-gray-600" />
                  </div>
                  <h3 className="text-gray-900 font-semibold text-sm sm:text-base truncate">MTA 차트 확대</h3>
                </div>
                <button
                  onClick={() => setIsChartExpanded(false)}
                  className="w-7 h-7 sm:w-8 sm:h-8 bg-gray-100 hover:bg-gray-200 rounded-lg flex items-center justify-center transition-colors flex-shrink-0"
                >
                  <X className="w-4 h-4 sm:w-5 sm:h-5 text-gray-600" />
                </button>
              </div>

              {/* 범례 */}
              <div className="bg-gray-50 px-2 py-2 sm:px-4 sm:py-3 border-b min-w-0 overflow-x-hidden">
                <div className="flex items-center justify-center gap-2 sm:gap-4 md:gap-6 flex-wrap min-w-0">
                  <div className="flex items-center gap-1 sm:gap-1.5 flex-shrink-0">
                    <div className="w-3 h-0.5 sm:w-4 sm:h-1 bg-green-500" />
                    <span className="text-[10px] sm:text-xs font-medium text-gray-700 whitespace-nowrap">정상</span>
                  </div>
                  <div className="flex items-center gap-1 sm:gap-1.5 flex-shrink-0">
                    <div className="w-3 h-0.5 sm:w-4 sm:h-1 bg-blue-500" />
                    <span className="text-[10px] sm:text-xs font-medium text-gray-700 whitespace-nowrap">조기</span>
                  </div>
                  <div className="flex items-center gap-1 sm:gap-1.5 flex-shrink-0">
                    <div className="w-3 h-0.5 sm:w-4 sm:h-1 bg-red-500" />
                    <span className="text-[10px] sm:text-xs font-medium text-gray-700 whitespace-nowrap">지연</span>
                  </div>
                  <div className="flex items-center gap-1 sm:gap-1.5 flex-shrink-0">
                    <div className="w-3 h-0.5 sm:w-4 sm:h-1 bg-red-500" />
                    <span className="text-[10px] sm:text-xs font-medium text-gray-700 whitespace-nowrap">C-Line</span>
                  </div>
                </div>
              </div>

              {/* 확대된 차트 - 스크롤 가능 */}
              <div className="bg-white p-2 sm:p-4 min-w-0 overflow-x-hidden">
                {/* 스크롤 힌트 */}
                <div className="flex items-center justify-center gap-1 mb-2 text-[10px] sm:text-xs text-gray-500 min-w-0">
                  <span className="text-center truncate">← 좌우로 스크롤하여 전체 차트 확인 →</span>
                </div>

                {/* 스크롤 영역 (강화된 overflow 제약) */}
                <div
                  className="w-full min-w-0 overflow-x-auto scrollbar-thin"
                  style={{
                    width: '100%',
                    maxWidth: '100%',
                    minWidth: 0,
                    maxHeight: '85vh',
                    overflowY: 'auto',
                    boxSizing: 'border-box',
                    position: 'relative',
                    isolation: 'isolate'
                  }}
                >
                  <div
                    className="apexcharts-wrapper"
                    style={{
                      width: 'max-content',
                      minWidth: '500px', // 더 작은 최소 너비
                      maxWidth: 'none',
                      boxSizing: 'border-box',
                      display: 'block',
                      contain: 'layout style'
                    }}
                  >
                    <Chart
                      options={options}
                      series={[...milestoneSeries, completionLine]}
                      type="line"
                      width="100%"
                      height={Math.min(viewportSize.height * 0.7, viewportSize.width < 640 ? 400 : viewportSize.width < 768 ? 500 : 600)}
                    />
                  </div>
                </div>
              </div>

              {/* 하단 안내 */}
              <div className="bg-gray-50 px-2 py-2 sm:px-4 sm:py-3 text-[10px] sm:text-xs text-gray-600 text-center border-t min-w-0 overflow-x-hidden">
                <p className="truncate">💡 마일스톤의 추세를 확인하여 프로젝트 진행 상황을 파악할 수 있습니다.</p>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
